import { useEffect, useState } from "react"
import { GetReferredShopInput, GetReferredShopOutput, ReferredShop, UpdateReferredShopInput, UpdateReferredShopOutput } from "../../client/core"
import { Form, Alert, Spinner, OverlayTrigger, Popover, Row, Col } from "react-bootstrap"
import { RouteComponentProps, useLocation } from "react-router-dom"
import { Button, icon, variant } from "../form/Button"
import ImageUploader from "../form/ImageUploader"
import { Path } from "../../env"
import Autocomplete from "react-google-autocomplete"

export interface ShopReferralAmendmentProps extends RouteComponentProps {
    getReferredShop: (input: GetReferredShopInput) => Promise<GetReferredShopOutput>
    updateReferredShop: (input: UpdateReferredShopInput) => Promise<UpdateReferredShopOutput>
    onImageUpload: (key: string, file: File, options?: { contentType: string }) => Promise<any>
}

const ShopReferralAmendment: React.FC<ShopReferralAmendmentProps> = (props) => {

    const [shop, setShop] = useState<ReferredShop | null>()
    const [error, setError] = useState("")
    const [success, setSuccess] = useState("")
    const [isLoading, setIsLoading] = useState(false)
    const [logo, setLogo] = useState<File | null>(null)
    const [isEditingAddress, setIsEditingAddress] = useState(false)
    const location = useLocation();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const shopEmail = queryParams.get("shopemail");

        if (!shopEmail) {
            return
        }

        props.getReferredShop({
            email: shopEmail
        }).then(response => {
            if (!response.body) {
                return
            }

            setShop(response.body)
        })
    }, [])

    const handleSubmit = async () => {
        if (!shop) return

        setIsLoading(true)
        setError("")
        setSuccess("")

        try {
            if (logo) {
                const imageName = `referral/logos/${shop.email}/default`
                await props.onImageUpload(
                    imageName,
                    logo,
                    {
                        contentType: "image/jpeg",
                    }
                );
            }

            const response = await props.updateReferredShop({
                email: shop.email,
                name: shop.name,
                addressId: shop.addressId,
                phone: shop.phone,
                whatsapp: shop.whatsapp || "",
                facebook: shop.facebook || "",
                instagram: shop.instagram || "",
                description: shop.description
            })

            if (response.statusCode !== 200 && response.statusCode !== 204) {
                setError(`Failed to update shop details. Status: ${response.statusCode}`)
            } else {
                setSuccess("Shop details updated successfully!")
                props.history.replace(`${Path.ConfirmShopCreation}?status=ACCEPTED&shopname=${shop.name}&shopemail=${shop.email}&shopid=${shopId(shop)}&addressId=${shop.addressId}`)
            }
        } catch (err) {
            setError("An error occurred while updating shop details")
        } finally {
            setIsLoading(false)
        }
    }

    const shopId = (shop: ReferredShop) => {
        if (shop.instagram && shop.instagram.trim() !== "") {
            return shop.instagram
        }

        return shop.name.trim().replaceAll(" ", "-").toLowerCase()
    }

    const handleInputChange = (field: keyof ReferredShop, value: string) => {
        setShop(prev => prev ? {
            ...prev,
            [field]: value
        } : null)
    }

    const handleLogoChange = (file: File) => {
        setLogo(file)
    }

    const handleAddressChange = (place: google.maps.places.PlaceResult) => {
        if (place.place_id && place.formatted_address) {
            setShop(prev => prev ? {
                ...prev,
                addressId: place.place_id!,
                address: place.formatted_address!
            } : null)
            setIsEditingAddress(false)
        }
    }

    if (!shop) {
        return (
            <div style={{ textAlign: "center" }}><Spinner variant="success" animation="border" /></div>
        )
    }

    return (
        <Form>
            <Alert variant="danger" show={error !== ""}>
                {error}
            </Alert>
            <Alert variant="success" show={success !== ""}>
                {success}
            </Alert>

            <Form.Group controlId="logo" className="mb-3">
                <ImageUploader
                    name="Logo"
                    label="Upload shop logo"
                    onUpload={handleLogoChange}
                    initialImage={shop.logo}
                />
            </Form.Group>

            <Form.Group controlId="email" className="mb-3">
                <Row>
                    <Col xs={10}>
                        <Form.Label>Email</Form.Label>
                        <div style={{
                            padding: ".375rem .75rem",
                            minHeight: "38px",
                            display: "flex",
                            alignItems: "center"
                        }}>
                            {shop.email}
                        </div>
                    </Col>
                    <Col xs={2} className="d-flex align-items-end">
                        <OverlayTrigger
                            trigger="click"
                            placement="top"
                            rootClose
                            overlay={
                                <Popover id="email-info-popover">
                                    <Popover.Body>
                                        The contact email address can be changed once registered.
                                    </Popover.Body>
                                </Popover>
                            }
                        >
                            <Button
                                buttonvariant={variant.Secondary}
                                icon={icon.Info}
                                onClick={() => { }}
                            />
                        </OverlayTrigger>
                    </Col>
                </Row>
            </Form.Group>

            <Form.Group controlId="address" className="mb-3">
                <Row>
                    <Col xs={10}>
                        <Form.Label>Shop Address</Form.Label>
                        {!isEditingAddress ? (
                            <div style={{
                                padding: ".375rem .75rem",
                                minHeight: "38px",
                                display: "flex",
                                alignItems: "center"
                            }}>
                                {shop.address || "No address provided"}
                            </div>
                        ) : (
                            <Autocomplete
                                apiKey={process.env.REACT_APP_CORE_GOOGLE_PLACES_KEY!}
                                onPlaceSelected={handleAddressChange}
                                style={{
                                    width: "100%",
                                    padding: ".375rem .75rem",
                                    borderRadius: ".25rem",
                                    border: "1px solid #ced4da",
                                }}
                                options={{
                                    types: ["address"],
                                }}
                                placeholder="Search for a new address..."
                            />
                        )}
                    </Col>
                    <Col xs={2} className="d-flex align-items-end">
                        <Button
                            buttonvariant={variant.Primary}
                            name={isEditingAddress ? "Cancel" : "Edit"}
                            onClick={() => setIsEditingAddress(!isEditingAddress)}
                        />
                    </Col>
                </Row>
            </Form.Group>

            <Form.Group controlId="name" className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                    type="text"
                    value={shop.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                />
            </Form.Group>



            <Form.Group controlId="phone" className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                    type="text"
                    value={shop.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                />
            </Form.Group>

            <Form.Group controlId="whatsapp" className="mb-3">
                <Form.Label>Whatsapp</Form.Label>
                <Form.Control
                    type="text"
                    value={shop.whatsapp || ""}
                    onChange={(e) => handleInputChange("whatsapp", e.target.value)}
                />
            </Form.Group>

            <Form.Group controlId="facebook" className="mb-3">
                <Form.Label>Facebook</Form.Label>
                <Form.Control
                    type="text"
                    value={shop.facebook || ""}
                    onChange={(e) => handleInputChange("facebook", e.target.value)}
                />
            </Form.Group>

            <Form.Group controlId="instagram" className="mb-3">
                <Form.Label>Instagram</Form.Label>
                <Form.Control
                    type="text"
                    value={shop.instagram || ""}
                    onChange={(e) => handleInputChange("instagram", e.target.value)}
                />
            </Form.Group>

            <Form.Group controlId="description" className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={4}
                    value={shop.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                />
            </Form.Group>
            <Button
                buttonvariant={variant.Primary}
                icon={isLoading ? icon.Spinner : icon.RightArrow}
                onClick={handleSubmit}
                name={isLoading ? "Generating site" : "Submit"}
                disabled={isLoading}
                style={{ width: "100%", marginTop: "1rem" }}
            />
        </Form>
    )

}

export default ShopReferralAmendment